import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Card from "../../../components/ui/card";
import { SquareCheckBig, X } from "lucide-react";
import {
    createGeneralSetting,
    getAllCurrencies,
    getAllLanguages,
    getAllTimezones,
    getUserGeneralSetting,
    UpdateGeneralSettings,
    useAppDispatch,
} from "../../../redux";
import { hideLoader, showLoader } from "../../../redux/Slices/loadingSlice";
import { useSelector } from "react-redux";
import LanguageDropdown from "../../../components/ui/LanguageDropdown";
import TimezoneDropdown from "../../../components/ui/TimezoneDropdown";
import CurrencyDropdown from "../../../components/ui/CurrencyDropdown";
import { Language } from "../../shared/model/LanguageInterfaces";
export default function GeneralSetting() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [errors, setErrors] = useState({
    preferredLanguages: "",
    timezone: "",
    currency: "",
});
    const { user, timezones, languages, currencies, generalSetting } = useSelector((state: any) => state.auth);
    
    // Helper function to map currency data to CurrencyDropdown format
    const mapCurrencyForDropdown = (currencyId: string) => {
        if (!currencyId || !currencies) return null;
        const foundCurrency = currencies.find((curr: any) => curr.value === currencyId || curr.id === currencyId);
        if (!foundCurrency) return null;
        return {
            id: foundCurrency.value || foundCurrency.id,
            name: foundCurrency.label || foundCurrency.name,
            code: foundCurrency.initial || foundCurrency.code || foundCurrency.value
        };
    };
    
    const [communication, setCommunication] = useState({
        preferredLanguages: [] as Language[],
        timezone: "",
        currency: "",
    });
useEffect(() => {
    if (generalSetting) {
        // Map languageIds to Language objects
        const selectedLanguages = generalSetting?.languageIds?.map(langId => {
            const language = languages?.find(l => l.id === langId.id);
            return language ? { id: language.id, name: language.name, initial: language.initial } : null;
        }).filter(Boolean) as Language[] || [];
        
        setCommunication({
            preferredLanguages: selectedLanguages,
            timezone: generalSetting?.timezoneId?.id || "",
            currency: generalSetting?.currencyId?.id || "",
        });
    }
}, [generalSetting, languages]);
    useEffect(() => {
        getAllFieldsValue();
    }, []);
    const getAllFieldsValue = async () => {
        dispatch(showLoader());
        await dispatch(getAllCurrencies({ isShowLoader: false }));
        await dispatch(getAllLanguages({ isShowLoader: false }));
        await dispatch(getAllTimezones({ isShowLoader: false }));
        await dispatch(getUserGeneralSetting({ id: user.id, isShowLoader: false }));
        dispatch(hideLoader());
    };
const handleSubmit = () => {
    const { preferredLanguages, timezone, currency } = communication;
    const newErrors = {
        preferredLanguages: preferredLanguages.length > 0 ? "" : t("generalSettings.validation.languageRequired"),
        timezone: timezone ? "" : t("generalSettings.validation.timezoneRequired"),
        currency: currency ? "" : t("generalSettings.validation.currencyRequired"),
    };
    setErrors(newErrors);
    // Stop if any errors exist
    const hasErrors = Object.values(newErrors).some(error => error !== "");
    if (hasErrors) return;
    const payload = {
        userId: {
            id: user?.id,
        },
        currencyId: {
            id: currency,
        },
        timezoneId: {
            id: timezone,
        },
        languageIds: preferredLanguages.map(lang => ({ id: lang.id })),
    };
    if (generalSetting != null) {
        dispatch(UpdateGeneralSettings({ id: generalSetting?.id, data: payload }));
    } else {
        dispatch(createGeneralSetting({ data: payload }));
    }
};
    return (
        <>
            {/* General Settings */}
            <Card color={undefined} className="p-6">
                <h2 className="text-xl font-semibold mb-6">{t("generalSettings.title")}</h2>
                {/* Language Preferences */}
                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{t("generalSettings.preferredLanguages")}</label>
                        
                        {/* Language Dropdown */}
                        <LanguageDropdown
                            multiple={true}
                            selectedLanguages={communication.preferredLanguages}
                            onLanguageSelect={() => {}} // Not used in multiple mode
                            onLanguagesSelect={(languages) => {
                                setCommunication(prev => ({ ...prev, preferredLanguages: languages }));
                            }}
                            placeholder={t("generalSettings.selectLanguagesPlaceholder")}
                            className="mb-3"
                        />
                        
                        {/* Selected Languages Tags */}
                        {communication.preferredLanguages.length > 0 && (
                            <div className="mt-3">
                                <p className="text-sm text-gray-600 mb-2">{t("generalSettings.selectedLanguages")}</p>
                                <div className="flex flex-wrap gap-2">
                                    {communication.preferredLanguages.map((language) => (
                                        <div
                                            key={language.id}
                                            className="inline-flex items-center gap-2 px-3 py-2 bg-blue-100 text-blue-800 rounded-full text-sm border border-blue-200 hover:bg-blue-200 transition-colors"
                                        >
                                            <span className="font-medium">{language.name}</span>
                                            <span className="text-xs bg-blue-200 px-1.5 py-0.5 rounded-full">
                                                {language.initial}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const updatedLanguages = communication.preferredLanguages.filter(
                                                        lang => lang.id !== language.id
                                                    );
                                                    setCommunication(prev => ({ 
                                                        ...prev, 
                                                        preferredLanguages: updatedLanguages 
                                                    }));
                                                }}
                                                className="ml-1 hover:bg-blue-300 rounded-full p-0.5 transition-colors group"
                                                title={t("generalSettings.removeLanguage")}
                                            >
                                                <X className="h-3 w-3 group-hover:text-red-600" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        
                        {errors.preferredLanguages && <p className="text-red-500 text-sm mt-1">{errors.preferredLanguages}</p>}
                    </div>
                    {/* Timezone */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{t("generalSettings.timezone")}</label>
                        <TimezoneDropdown
                            selectedTimezone={timezones?.find(tz => tz.id === communication.timezone || tz.value === communication.timezone)}
                            onTimezoneSelect={(timezone) => {
                                setCommunication(prev => ({ 
                                    ...prev, 
                                    timezone: timezone?.id || timezone?.value || "" 
                                }));
                            }}
                            placeholder={t("generalSettings.selectTimezonePlaceholder")}
                            className="mb-3"
                            autoDetect={true}
                        />
                        {errors.timezone && <p className="text-red-500 text-sm mt-1">{errors.timezone}</p>}
                    </div>
                    {/* Currency */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{t("generalSettings.preferredCurrency")}</label>
                        <CurrencyDropdown
                            selectedCurrency={mapCurrencyForDropdown(communication.currency)}
                            onCurrencySelect={(currency) => {
                                setCommunication(prev => ({ 
                                    ...prev, 
                                    currency: currency?.id || "" 
                                }));
                            }}
                            placeholder={t("generalSettings.selectCurrencyPlaceholder")}
                            autoDetect={true}
                            className="mb-3"
                            placement="top"
                        />
                        {errors.currency && <p className="text-red-500 text-sm mt-1">{errors.currency}</p>}
                    </div>
                </div>
                {/* Danger Zone */}
            </Card>
            <div className="flex justify-end mt-4">
                <button className={"px-4 py-2 text-white rounded-md flex items-center bg-blue-600 hover:bg-blue-700"} onClick={handleSubmit}>
                    <SquareCheckBig className="h-5" />
                    <span className="pl-2">
                        {generalSetting!=null? t("generalSettings.updateChanges") : t("generalSettings.saveChanges")}
                       </span>
                </button>
            </div>
        </>
    );
}